import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPlay } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { useDownloadLibraryAssetMutation } from "@/pages/Private/redux/media/media.api";
import { LibraryAsset } from "@/pages/Private/redux/media/media.schema";
import { classNames } from "@/utils/classNames";

import { LIBRARY_ITEM_DRAG_TYPE } from "./MediaFolderTree";

interface MediaAssetCardProps {
	asset: LibraryAsset;
	/** The scenario being browsed from. Without one there is nowhere to assign anything. */
	courseId?: number;
	isBusy?: boolean;
	onAssign: (asset: LibraryAsset, fileAssignment: string) => void;
	onUnassign: (courseFileId: number) => void;
	onDelete: (asset: LibraryAsset) => void;
	/** Opens the file large, in the library's lightbox — a video is watched, not squinted at. */
	onPreview: (asset: LibraryAsset) => void;
	/** Lets the card be picked up and dropped on a folder in the tree beside the grid. */
	draggable?: boolean;
}

const formatBytes = (bytes?: number | null) => {
	if (!bytes) {
		return "—";
	}

	return bytes > 1024 * 1024
		? `${(bytes / 1024 / 1024).toFixed(1)} MB`
		: `${Math.round(bytes / 1024)} KB`;
};

/**
 * One generated file, what it is, and what it is doing.
 *
 * The preview is the real file rather than a thumbnail — there are no thumbnails, and a
 * card that cannot show you the video is not much use for deciding which video you want.
 * Audio and video are `preload="metadata"` so a library of thirty files does not download
 * thirty videos to render a grid.
 */
export const MediaAssetCard: FunctionComponent<MediaAssetCardProps> = ({
	asset,
	courseId,
	isBusy,
	onAssign,
	onUnassign,
	onDelete,
	onPreview,
	draggable = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`courses.mediaLibrary.${key}`, options ?? {});

	const [downloadAsset] = useDownloadLibraryAssetMutation();
	const [downloadError, setDownloadError] = useState("");

	const handleDownload = async () => {
		setDownloadError("");

		try {
			const { blob, filename } = await downloadAsset({
				kind: asset.kind === "uploaded" ? "uploaded" : "generated",
				id: asset.id,
			}).unwrap();
			const url = URL.createObjectURL(blob);
			const anchor = document.createElement("a");

			anchor.href = url;
			anchor.download = filename;
			anchor.click();
			URL.revokeObjectURL(url);
		} catch {
			// An authenticated request can fail where the old direct link could not — and a
			// click that silently does nothing reads as a broken button.
			setDownloadError(ts("downloadFailed"));
		}
	};

	const mimeType = asset.mimeType ?? "";
	const isVideo = mimeType.startsWith("video/");
	const isImage = mimeType.startsWith("image/");
	const isAudio = mimeType.startsWith("audio/");

	// There is deliberately no "use as phase content" here. Phase content is a URL held by
	// the phase, so putting a file on one needs a phase to put it on — which this card has
	// no notion of. The phase editor picks *from* the library instead; a button here could
	// only ever have created a course-file nothing pointed at, which is exactly what it did.

	// Where an assignment would land: the scenario being browsed from, or — on the
	// standalone page — the scenario that generated the file. A rendered export's home
	// is known even when the page has no scenario of its own.
	const assignCourseId = courseId ?? asset.sourceCourseId ?? undefined;

	// What this file is doing *there* — the question the author is actually asking — and
	// separately whether other scenarios rely on it, which is what makes deletion refuse.
	const hereUsage = assignCourseId
		? asset.usages.find((usage) => usage.courseId === assignCourseId)
		: undefined;
	// On the standalone page every use is "elsewhere", which is exactly what wants showing.
	const elsewhere = asset.usages.filter((usage) => usage.courseId !== courseId);
	const isTrailerHere = hereUsage?.fileAssignment === "TrailerVideo";
	const isForeign = asset.sourceCourseId !== courseId;

	return (
		<article
			className={classNames(
				"flex flex-col gap-2 rounded-card border border-border-soft bg-white p-3",
				draggable && "cursor-grab active:cursor-grabbing"
			)}
			draggable={draggable}
			onDragStart={(event) => {
				// The library's own type, so a folder row knows this is a file and not a picture
				// dragged out of the page. Kind and id are all a move needs.
				event.dataTransfer.setData(
					LIBRARY_ITEM_DRAG_TYPE,
					JSON.stringify({ kind: asset.kind, id: asset.id })
				);
				event.dataTransfer.effectAllowed = "move";
			}}
		>
			<div className="relative flex h-32 items-center justify-center overflow-hidden rounded-card bg-surface">
				{/* A video's poster is exactly its first frame — for scene videos, the styled
				    scene image — so an image tile and a video tile can look identical. The
				    kind badge is what tells them apart at a glance. */}
				<span className="absolute left-1.5 top-1.5 z-10 rounded bg-ink/80 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
					{isVideo
						? ts("kindVideo")
						: isImage
							? ts("kindImage")
							: isAudio
								? ts("kindAudio")
								: ts("kindFile")}
				</span>
				{isImage && (
					// Not draggable on its own: the card is the thing that gets dragged, and a
					// picture that started its own drag would carry no library entry with it.
					<img
						alt={asset.fileName ?? ""}
						className="max-h-32 w-full object-contain"
						draggable={false}
						src={asset.fullFilePath}
					/>
				)}
				{isVideo && (
					// The tile only shows the poster; playing happens in the lightbox, where a
					// video is big enough to actually watch.
					<button
						className="group relative h-full w-full"
						type="button"
						onClick={() => onPreview(asset)}
					>
						<video
							className="pointer-events-none h-full w-full object-contain"
							preload="metadata"
							src={asset.fullFilePath}
						/>
						<span className="absolute inset-0 flex items-center justify-center">
							<span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink/70 text-white group-hover:bg-ink/90">
								<Icon icon={faPlay} />
							</span>
						</span>
					</button>
				)}
				{isAudio && (
					<audio controls className="w-full px-2" preload="metadata" src={asset.fullFilePath} />
				)}
				{!isImage && !isVideo && !isAudio && (
					<span className="text-xs text-subtle">{ts("noPreview")}</span>
				)}
			</div>

			<div className="flex flex-wrap items-center gap-1.5">
				<span className="rounded-full bg-surface px-2 py-0.5 text-xs font-semibold text-subtle">
					{/* An uploaded entry has no job behind it, so there is no job type to name —
					    a style reference says what it defines, any other upload stays "uploaded". */}
					{asset.jobType
						? t(`courses.jobsCosts.type.${asset.jobType}`)
						: asset.styleReference
							? ts("origin.styleReference")
							: ts("origin.uploaded")}
				</span>
				{isTrailerHere && (
					<span className="rounded-full bg-success-light px-2 py-0.5 text-xs font-semibold text-text">
						{ts("roleTrailer")}
					</span>
				)}
				{!!hereUsage && !isTrailerHere && (
					<span className="rounded-full bg-accent-tint px-2 py-0.5 text-xs font-semibold text-ink">
						{ts("roleContent")}
					</span>
				)}
				{!hereUsage && !elsewhere.length && (
					<span className="rounded-full bg-surface px-2 py-0.5 text-xs font-semibold text-subtle">
						{ts("roleUnused")}
					</span>
				)}
				{!!elsewhere.length && (
					<span
						className="rounded-full bg-status-running-light px-2 py-0.5 text-xs font-semibold text-warning-ink"
						title={elsewhere.map((usage) => usage.courseName).join(", ")}
					>
						{ts("roleElsewhere", { times: elsewhere.length })}
					</span>
				)}
			</div>

			<p className="truncate text-xs text-subtle" title={asset.fileName ?? ""}>
				{asset.width ? `${asset.width}×${asset.height} · ` : ""}
				{formatBytes(asset.fileLength)}
				{isForeign && asset.sourceCourseName
					? ` · ${ts("from", { name: asset.sourceCourseName })}`
					: ""}
			</p>

			<div className="mt-auto flex flex-wrap gap-1.5">
				{isVideo && !isTrailerHere && !!assignCourseId && (
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={isBusy}
						size={ButtonSize.XS}
						title={ts("useAsTrailer")}
						type="button"
						onClick={() => onAssign(asset, "TrailerVideo")}
					/>
				)}
				{!!hereUsage && !!assignCourseId && (
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={isBusy}
						size={ButtonSize.XS}
						title={ts("removeFromScenario")}
						type="button"
						onClick={() => onUnassign(hereUsage.courseFileId)}
					/>
				)}
				{/* A real save-as through the API — a link to the bucket could only open a
				    tab, where a video shows as its paused first frame. Styled as its
				    neighbours: the same Button the other card actions use. */}
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					disabled={isBusy}
					size={ButtonSize.XS}
					title={ts("download")}
					type="button"
					onClick={handleDownload}
				/>
				{!asset.usages.length && (
					<Button
						color={ButtonColor.DANGER}
						disabled={isBusy}
						size={ButtonSize.XS}
						title={ts("delete")}
						type="button"
						onClick={() => onDelete(asset)}
					/>
				)}
			</div>

			{!!downloadError && (
				<p className="mt-1.5 text-xs font-semibold text-danger">{downloadError}</p>
			)}
		</article>
	);
};
